import React from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Stack,
  useTheme,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/**
 * A styled stats card component with icon and value
 *
 * @param {Object} props - Component props
 * @param {String|Number} props.value - The main value to display
 * @param {String} props.label - The label for the value
 * @param {String} props.subtitle - Optional subtitle text for additional context
 * @param {Node} props.icon - The icon to display
 * @param {String} props.color - Color theme (primary, secondary, success, warning, error, info)
 * @param {String} props.trend - Optional trend indicator (up, down, neutral)
 * @param {String|Number} props.trendValue - Optional trend value
 * @param {Object} props.sx - Additional styling for the card
 */
const StatsCard = ({
  value,
  label,
  subtitle,
  icon,
  color = "primary",
  trend,
  trendValue,
  onClick,
  sx = {},
}) => {
  const theme = useTheme();

  // Determine colors based on color prop
  const getColorStyles = () => {
    const colorMap = {
      primary: {
        light: "rgba(25, 118, 210, 0.08)",
        main: theme.palette.primary.main,
        avatar: theme.palette.primary.main,
        border: "rgba(25, 118, 210, 0.12)",
      },
      secondary: {
        light: "rgba(156, 39, 176, 0.08)",
        main: theme.palette.secondary.main,
        avatar: theme.palette.secondary.main,
        border: "rgba(156, 39, 176, 0.12)",
      },
      success: {
        light: "rgba(76, 175, 80, 0.08)",
        main: theme.palette.success.main,
        avatar: theme.palette.success.main,
        border: "rgba(76, 175, 80, 0.12)",
      },
      warning: {
        light: "rgba(255, 152, 0, 0.08)",
        main: theme.palette.warning.main,
        avatar: theme.palette.warning.main,
        border: "rgba(255, 152, 0, 0.12)",
      },
      error: {
        light: "rgba(244, 67, 54, 0.08)",
        main: theme.palette.error.main,
        avatar: theme.palette.error.main,
        border: "rgba(244, 67, 54, 0.12)",
      },
      info: {
        light: "rgba(33, 150, 243, 0.08)",
        main: theme.palette.info.main,
        avatar: theme.palette.info.main,
        border: "rgba(33, 150, 243, 0.12)",
      },
    };

    return colorMap[color] || colorMap.primary;
  };

  const colors = getColorStyles();

  // Determine trend icon and color
  const getTrendStyles = () => {
    if (!trend) return null;

    const trendMap = {
      up: {
        color: theme.palette.success.main,
        icon: "↑",
      },
      down: {
        color: theme.palette.error.main,
        icon: "↓",
      },
      neutral: {
        color: theme.palette.text.secondary,
        icon: "→",
      },
    };

    return trendMap[trend] || trendMap.neutral;
  };

  const trendStyles = getTrendStyles();

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: colors.light,
        border: `1px solid ${colors.border}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          boxShadow: 2,
          transform: 'translateY(-2px)',
          borderColor: colors.main,
        } : {},
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar
          sx={{
            bgcolor: colors.avatar,
            width: 48,
            height: 48,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography color="text.secondary">{label}</Typography>
            {subtitle && (
              <Tooltip
                title={subtitle}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: "0.8rem",
                      bgcolor: "rgba(0, 0, 0, 0.8)",
                      "& .MuiTooltip-arrow": {
                        color: "rgba(0, 0, 0, 0.8)",
                      },
                      p: 1.2,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    ml: 0.5,
                    p: 0.5,
                    color: "text.secondary",
                    "&:hover": {
                      color: colors.main,
                    },
                  }}
                >
                  <InfoIcon sx={{ fontSize: "1.2rem" }} />
                </IconButton>
              </Tooltip>
            )}
            {trendStyles && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: 1,
                }}
              >
                <Typography
                  sx={{
                    color: trendStyles.color,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.875rem",
                  }}
                >
                  {trendStyles.icon} {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {onClick && (
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            sx={{
              color: colors.main,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.875rem',
              minWidth: 'auto',
              '&:hover': {
                bgcolor: 'transparent',
                color: colors.avatar,
              },
            }}
          >
            View
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default StatsCard;
